import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Drawer, Input, Button, Icon, Spin } from "ant-design-vue";
import { ConversationTestWidgetLocale } from "@/locales/localeid";
import Spinning from "@/components/spinning/spinning.vue";
import { ConversationPublishStatus, fmtMsg, MessageHelper, NotificationType } from "commonui";
import {
	ConversationAuthorType,
	ConversationStatus,
	ConversationTag,
	ConversationTemplateType,
	PathTypes,
	SpeechRecognizerResultReason
} from "@/util/enum";
import { AzureLuisCognitiveService, IAzureLuisCognitiveService } from "@/services/azure-luis-cognitive-service";
import {
	ConversationBuilderService,
	ConversationChatModel,
	ConversationDialogChatModel,
	ConversationAlternateDialogChatModel,
	ConversationPathChatModel,
	ConversationUtterenceChatModel,
	ConversationPathEntityValueChatModel,
	QueryCLURequestModel,
	ConversationChatModelV2
} from "@/services/conversation-builder";
import { isNull, isUndefined, uniq } from "lodash";
import { AdminService, ContainerType, SignatureResoureModel } from "@/services/admin";
import {
	ChildNameForConversation,
	NonePathValue,
	TitleForConversation,
	TopIntentMinScore,
	ZeroScore
} from "@/util/constants";
import { AudioConfig, ResultReason, SpeechConfig, SpeechRecognizer } from "microsoft-cognitiveservices-speech-sdk";
import { AzureSpeechRecognizerCognitiveService } from "@/services/azure-speech-recognizer-cognitive-service";
import ConversationTemplateState from "@/modules/conversation-template";
import { getModule } from "vuex-module-decorators";
import { getCLUPredictionResponseAsLUISResponse, getConversationNextDialogId, getDayOfTheWeek, isOldConversationImage } from "@/util/func";
import { ConversationBuilderState } from "@/modules/conversation-builder";
export interface ConversationTestWidgetProps {
	conversationId?: number;
}

interface TestConversationModel {
	body: string;
	imageUrl?: string;
	author: ConversationAuthorType;
}

interface ConversationScoreModel {
	body: string;
	score: string;
}

@Component({
	components: {
		Drawer,
		Spinning,
		TextArea: Input.TextArea,
		Button,
		Icon,
		Input,
		Spin
	}
})
export default class ConversationTestWidget extends Vue {
	@Prop({ default: 0 }) conversationId!: number;
	@Prop({ default: true }) disabled!: boolean;
	@Prop({ default: null }) createOrPublishConveration!: () => Promise<void>;
	@Prop({ default: false }) isValidatingMetadataForTest!: boolean;
	private readonly noneNextDialogId = 0;
	private readonly retryDialodId = -1;
	public isDrawerVisible = false;
	public drawerTitle = fmtMsg(ConversationTestWidgetLocale.ConversationWidgetTitle);
	public initLoading = false;
	public isLoadingMachineMessage = false;
	public inputText = "";
	public conversations: TestConversationModel[] = [];
	public azureLuisCognitiveService: IAzureLuisCognitiveService | null = null;
	public conversationBuilderService = ConversationBuilderService;
	public conversationInfo: ConversationChatModel | ConversationChatModelV2 | null = null;
	public currentDialogId: number = 0;
	public adminService = AdminService;
	private signatureBase!: string;
	private signatureParams!: string;
	private signatureBaseForContent!: string;
	private signatureParamsForContent!: string;
	public inputDisabled: boolean = false;
	public recognizing = false;
	private recognizer: SpeechRecognizer | null = null;
	public showDetails = false;
	public converationScores: ConversationScoreModel[] = [];
	public templateState = getModule(ConversationTemplateState);
	public azureSpeechRecognizerCognitiveService;
	private conversationStore = getModule(ConversationBuilderState);
	public currentResponse: any = {};

	created() {
		if (!this.templateState.conversationTags.length) {
			this.templateState.fetchConversationTags();
		}
	}

	get locale() {
		return ConversationTestWidgetLocale;
	}

	setInitialConversation(conversationInfo: ConversationChatModel | ConversationChatModelV2) {
		const { intents } = this.conversationStore;
		/*
				1. If there are no intents but conversation is published - only dialogs would be shown 
				2. If intents are present then we need to check endpoint, key and project are not falsy since we want query clu.
			*/
		if (
			(isUndefined(conversationInfo) ||
				isNull(conversationInfo) ||
				!(
					(conversationInfo as ConversationChatModelV2).publishStatusId === ConversationPublishStatus.Deployed
				) ||
				conversationInfo.statusId === ConversationStatus.Deleted) &&
			(!!Object.values(intents.directory).length ||
				(Object.values(intents.directory).length &&
					(conversationInfo as ConversationChatModelV2).endPoint == "") ||
				isNull((conversationInfo as ConversationChatModelV2).endPoint) ||
				(conversationInfo as ConversationChatModelV2).key == "" ||
				isNull((conversationInfo as ConversationChatModelV2).key) ||
				(conversationInfo as ConversationChatModelV2).projectName == "" ||
				isNull((conversationInfo as ConversationChatModelV2).projectName))
		) {
			MessageHelper.Message(NotificationType.Warning, fmtMsg(this.locale.ConversationInfoInvalidMessage));
			return;
		}

		const headDialog = conversationInfo.conversationDialog.find(
			(c) => c.isHead && c.statusId === ConversationStatus.Active
		);

		if (isUndefined(headDialog)) {
			this.endConversation(fmtMsg(this.locale.ConversationNoHeadMessage));
			return;
		}

		const projectedConversations: TestConversationModel[] = [];
		projectedConversations.push({
			body: headDialog.value,
			author: ConversationAuthorType.Machine,
			imageUrl: this.getImageUrl(headDialog.imageUrl)
		});

		const currentDialogId = headDialog.id;
		const nextDialogId = headDialog.nextDialogId;
		this.currentDialogId = currentDialogId;
		this.getTargetDialogs(nextDialogId, currentDialogId, projectedConversations);

		this.conversations = [...this.conversations, ...projectedConversations];
	}

	getTargetDialogs(nextDialogId, currentDialogId, projectedConversations) {
		let nextId = nextDialogId;
		let currentId = currentDialogId;
		if (!isUndefined(nextId) && !isNull(nextId)) {
			const nextConversation = this.getActiveDialogById(nextId);
			if (!isUndefined(nextConversation) && !isNull(nextConversation)) {
				projectedConversations.push({
					body: nextConversation.value,
					author: ConversationAuthorType.Machine,
					imageUrl: this.getImageUrl(nextConversation.imageUrl)
				});
				currentId = nextConversation.id;
				nextId = nextConversation.nextDialogId;
				this.currentDialogId = currentId;
				if (isNull(nextId) && nextConversation.path.length === 0) {
					projectedConversations.push({
						body: fmtMsg(this.locale.ConversationEndMessage),
						author: ConversationAuthorType.Machine,
						imageUrl: ""
					});
					this.inputDisabled = true;
				}
				this.getTargetDialogs(nextId, currentId, projectedConversations);
			}
		}
	}

	getActiveDialogById(id) {
		return this.conversationInfo?.conversationDialog.find(
			(c) => c.id === id && c.statusId === ConversationStatus.Active
		);
	}

	private setSasUrl(data: SignatureResoureModel[]) {
		//handle the old logic: Image clone instead of reference
		const pageSignature = data?.find((signature) => signature.type === ContainerType.File);
		if (pageSignature) {
			const splitedUrl = pageSignature.url.split("?");
			this.signatureBase = splitedUrl[0] + pageSignature.name + "/";
			this.signatureParams = "?" + splitedUrl[1];
		}
		const pageSignatureForContent = data?.find((signature) => signature.type === ContainerType.Page);
		if (pageSignatureForContent) {
			const splitedUrl = pageSignatureForContent.url.split("?");
			this.signatureBaseForContent = splitedUrl[0] + pageSignatureForContent.name + "/";
			this.signatureParamsForContent = "?" + splitedUrl[1];
		}
	}

	setIsLoadingMachineMessage(value: boolean) {
		this.isLoadingMachineMessage = value;
	}

	private getImageUrl(path: string | null) {
		if (isNull(path) || path == "") {
			return "";
		}
		if (isOldConversationImage(path)) {
			return this.signatureBase + path + this.signatureParams;
		}
		return this.signatureBaseForContent + path + this.signatureParamsForContent;
	}

	testConversation() {
		if (isUndefined(this.conversationId) || isNull(this.conversationId) || this.conversationId <= 0) {
			MessageHelper.Message(NotificationType.Warning, fmtMsg(this.locale.ConversationIdInvalidMessage));
			return;
		}
		this.isDrawerVisible = true;
		this.setInitLoading(true);
		this.adminService
			.getDownloadSasURL()
			.then((urlInfo) => {
				this.setSasUrl(urlInfo);
				this.conversationBuilderService
					.getConversationInfo(this.conversationId)
					.then((data) => {
						this.conversationInfo = data;
						const { speechKey, speechRegion } = data.speechConfig;
						this.azureSpeechRecognizerCognitiveService = new AzureSpeechRecognizerCognitiveService(
							speechKey,
							speechRegion
						);
						this.setInitialConversation(data);
						this.setInitLoading(false);
					})
					.catch((err) => {
						this.setInitLoading(false);
						this.unableToLoadMessage();
					});
			})
			.catch((err) => {
				this.setInitLoading(false);
				this.unableToLoadMessage();
			});
	}

	get authorType() {
		return ConversationAuthorType;
	}

	setInitLoading(value: boolean) {
		this.initLoading = value;
	}

	onDetailsDrawerClose() {
		this.showDetails = false;
		const viewDetailsButton = document.getElementById("view-details-button");
		if (viewDetailsButton) {
			viewDetailsButton.focus();
		}
	}

	viewDetails() {
		this.showDetails = true;
		setTimeout(() => {
			const detailsDrawer = document.getElementById("details-drawer");
			if (detailsDrawer) {
				detailsDrawer.focus();
			}
		}, 100);
	}

	addToConversation(text: string, type: ConversationAuthorType, url: string | null) {
		this.conversations.push({
			body: text,
			author: type,
			imageUrl: this.getImageUrl(url)
		});
	}

	addConversationScore(queryText, score) {
		const conversationScore: ConversationScoreModel = {
			body: queryText,
			score: score + "%"
		};

		this.converationScores = [...this.converationScores, conversationScore];
	}

	convertScoreToString(score) {
		return (score * 100).toFixed(2).toString();
	}

	async onInputPressEnter(speechText?: string | null) {
		const queryText = isUndefined(speechText) || isNull(speechText) ? this.inputText.trim() : speechText.trim();

		if (queryText === "") {
			return;
		}

		this.addToConversation(queryText, ConversationAuthorType.User, null);
		this.inputText = "";
		this.scrollChatArea();
		this.setIsLoadingMachineMessage(true);

		const requestData: QueryCLURequestModel = {
			deploymentName: (this.conversationInfo as ConversationChatModelV2).deploymentName as string,
			projectName: (this.conversationInfo as ConversationChatModelV2).projectName,
			endPoint: (this.conversationInfo as ConversationChatModelV2).endPoint as string,
			key: (this.conversationInfo as ConversationChatModelV2).key as string,
			queryText
		};
		this.currentResponse = await this.conversationBuilderService.queryCLU(requestData);
		const { topIntentObj, transformedEntities } = getCLUPredictionResponseAsLUISResponse(this.currentResponse);
		const topIntent = this.currentResponse.prediction.topIntent;

		this.moveToNext(
			getConversationNextDialogId(
				topIntent,
				topIntentObj,
				queryText,
				transformedEntities,
				this.getActiveDialogById,
				this.currentDialogId,
				this.conversationInfo,
				getCLUPredictionResponseAsLUISResponse,
				this.getRetryOrFallbackId,
				this.currentResponse,
				this.noneNextDialogId,
				this.getUtteranceValues,
				{
					addNoneScore: this.addNoneScore,
					addConversationScore: this.addConversationScore,
					utteranceUseEntity: false
				}
			)[0],
			ConversationTemplateType.Regular
		);
	}

	moveToNext(nextDialogId: number, conversationTemplateType: ConversationTemplateType, endMsg?) {
		if (nextDialogId !== this.noneNextDialogId && nextDialogId !== this.retryDialodId) {
			const projectedDialog = this.getActiveDialogById(nextDialogId);
			if (!isUndefined(projectedDialog)) {
				const projectedConversations: TestConversationModel[] = [];
				projectedConversations.push({
					body: projectedDialog.value,
					author: ConversationAuthorType.Machine,
					imageUrl: this.getImageUrl(projectedDialog.imageUrl)
				});

				const currentDialogId = projectedDialog.id;
				this.currentDialogId = currentDialogId;

				if (isNull(projectedDialog.nextDialogId) && projectedDialog.path.length === 0) {
					projectedConversations.push({
						body: fmtMsg(this.locale.ConversationEndMessage),
						author: ConversationAuthorType.Machine,
						imageUrl: ""
					});
					this.inputDisabled = true;
				} else if (!isNull(projectedDialog.nextDialogId)) {
					const nextId = projectedDialog.nextDialogId;
					this.getTargetDialogs(nextId, currentDialogId, projectedConversations);
				}

				this.setIsLoadingMachineMessage(false);
				this.conversations = [...this.conversations, ...projectedConversations];
				this.scrollChatArea();
			} else {
				this.setIsLoadingMachineMessage(false);
				this.endConversation(endMsg);
				this.scrollChatArea();
			}
		} else if (nextDialogId === this.retryDialodId) {
			const currentRetryCount = this.updateDialogRetryCount(this.currentDialogId, conversationTemplateType);
			const currentDialog = this.getActiveDialogById(this.currentDialogId);
			this.setIsLoadingMachineMessage(false);
			if (!isUndefined(currentDialog) && !isNull(currentDialog) && currentRetryCount > 0) {
				const alternativeTexts = currentDialog.alternateDialog.filter(
					(d) => d.dialogTypeId === conversationTemplateType && d.statusId === ConversationStatus.Active
				);

				if (alternativeTexts.length > 0) {
					const alternativeTextObject:
						| ConversationAlternateDialogChatModel
						| undefined = this.getAlternateDialogByCurrentRetryCount(alternativeTexts, currentRetryCount);
					if (!isUndefined(alternativeTextObject)) {
						this.addToConversation(
							this.replaceTagsFromAlternateDialog(alternativeTextObject.value),
							ConversationAuthorType.Machine,
							currentDialog.imageUrl
						);
					} else {
						this.endConversation(endMsg);
					}
				} else {
					this.addToConversation(currentDialog.value, ConversationAuthorType.Machine, currentDialog.imageUrl);
				}
			} else {
				this.endConversation(endMsg);
			}
			this.scrollChatArea();
		} else {
			this.setIsLoadingMachineMessage(false);
			this.endConversation(endMsg);
			this.scrollChatArea();
		}
	}

	getAlternateDialogByCurrentRetryCount(alternateDialog: ConversationAlternateDialogChatModel[], currentRetryCount) {
		if (!isUndefined(alternateDialog) && !isNull(alternateDialog) && alternateDialog.length > 0) {
			const condition1 = alternateDialog.find((a) => a.sequence === currentRetryCount);
			if (!isUndefined(condition1)) {
				return condition1;
			}

			const maxSequenceDialog = alternateDialog.reduce((p, c) => (p.sequence > c.sequence ? p : c));
			const condition2 = alternateDialog.find((a) => a.sequence === maxSequenceDialog.sequence);

			return condition2;
		} else {
			return undefined;
		}
	}

	replaceTagsFromAlternateDialog(dialogText) {
		let retStr = dialogText;
		const tags = this.templateState.conversationTags.map((t) => {
			return t.value;
		});

		for (const i in tags) {
			let value = "";
			switch (tags[i]) {
				case ConversationTag.Title:
					value = TitleForConversation;
					break;
				case ConversationTag.ChildName:
					value = ChildNameForConversation;
					break;
				case ConversationTag.DayOfWeek:
					value = getDayOfTheWeek();
					break;

				default:
					value = "";
					break;
			}
			retStr = retStr.replace(new RegExp("{" + tags[i] + "}", "g"), value);
		}

		return retStr;
	}

	updateDialogRetryCount(dialogId: number, conversationTemplateType: ConversationTemplateType): number {
		let currentRetryCount = 0;
		if (!isUndefined(this.conversationInfo) && !isNull(this.conversationInfo)) {
			const dialogIndex = this.conversationInfo.conversationDialog.findIndex((d) => d.id === dialogId);
			if (dialogIndex > -1) {
				currentRetryCount = this.conversationInfo.conversationDialog[dialogIndex].currentRetryCount =
					this.conversationInfo.conversationDialog[dialogIndex].currentRetryCount + 1;

				if (conversationTemplateType === ConversationTemplateType.Silent) {
					currentRetryCount = this.conversationInfo.conversationDialog[dialogIndex].currentSilentRetryCount =
						this.conversationInfo.conversationDialog[dialogIndex].currentSilentRetryCount + 1;
				} else if (conversationTemplateType === ConversationTemplateType.Regular) {
					currentRetryCount = this.conversationInfo.conversationDialog[
						dialogIndex
					].currentAlternateRetryCount =
						this.conversationInfo.conversationDialog[dialogIndex].currentAlternateRetryCount + 1;
				}
			}
		}

		return currentRetryCount;
	}

	unableToLoadMessage() {
		MessageHelper.Message(NotificationType.Failed, fmtMsg(this.locale.ConversationErrorLoadingMessage));
	}

	scrollChatArea() {
		this.$nextTick(() => {
			const messageDisplay: any = this.$refs.chatArea;
			if (messageDisplay) {
				messageDisplay.scrollTop = messageDisplay.scrollHeight;
			}
		});
	}

	getUtteranceValues(
		utterances: ConversationUtterenceChatModel[],
		pathEntityValues: ConversationPathEntityValueChatModel[]
	) {
		var utteranceValues: string[] = [];

		for (var i = 0; i < utterances.length; i++) {
			const currentUtterance = utterances[i];
			if (currentUtterance.statusId === ConversationStatus.Active) {
				if (!currentUtterance.isPattern) {
					utteranceValues.push(currentUtterance.value.toLocaleLowerCase());
				} else if (currentUtterance.isPattern && currentUtterance.entityId) {
					const retStr = currentUtterance.value;
					const currentEntityValues = pathEntityValues.filter(
						(p) => p.entityId === currentUtterance.entityId
					);

					for (var j = 0; j < currentEntityValues.length; j++) {
						const currentEntityValue = currentEntityValues[j];

						utteranceValues.push(
							retStr
								.replace(
									new RegExp("{" + currentEntityValue.entityName + "}", "g"),
									currentEntityValue.value
								)
								.toLocaleLowerCase()
						);
					}
				}
			}
		}

		return utteranceValues;
	}

	canRetry(currentDialog: ConversationDialogChatModel) {
		return currentDialog.retryCount > 0 && currentDialog.retryCount > currentDialog.currentRetryCount;
	}

	addNoneScore(queryText, topIntent, score) {
		if (topIntent === NonePathValue) {
			this.addConversationScore(queryText, ZeroScore);
		} else {
			this.addConversationScore(queryText, this.convertScoreToString(score));
		}
	}

	getFallbackPathId(currentDialog: ConversationDialogChatModel) {
		const fallbackPath = currentDialog?.path.find(
			(i) => i.isFallback && i.pathTypeId === PathTypes.Fallback && i.statusId === ConversationStatus.Active
		);
		if (!isUndefined(fallbackPath) && fallbackPath.nextDialogId) {
			return fallbackPath.nextDialogId;
		} else {
			return this.noneNextDialogId;
		}
	}

	endConversation(msg?) {
		this.inputDisabled = true;
		const msgToPublish = isUndefined(msg) ? fmtMsg(this.locale.ConversationEndErrorMessage) : msg;
		this.addToConversation(msgToPublish, ConversationAuthorType.Machine, null);
	}

	onDrawerClose() {
		this.isDrawerVisible = false;
		this.showDetails = false;
		this.inputDisabled = false;
		this.azureLuisCognitiveService = null;
		this.inputText = "";
		this.conversations = [];
		this.conversationInfo = null;
		this.currentDialogId = 0;
		this.recognizing = false;
		this.recognizer = null;
		this.converationScores = [];
	}

	startButton() {
		if (!this.recognizing) {
			this.recognizing = true;
			const audioConfig = AudioConfig.fromDefaultMicrophoneInput();
			this.recognizer = this.azureSpeechRecognizerCognitiveService.getSpeechRecognizer(
				audioConfig
			) as SpeechRecognizer;
			this.recognizer.recognizing = this.recognizer.recognized = this.recognizerCallback.bind(this);
			this.recognizer.recognizeOnceAsync(
				(cb) => {
					this.recognizing = false;
				},
				(err) => {
					MessageHelper.Message(NotificationType.Failed, fmtMsg(this.locale.ConversationNoSpeechMessage));
					this.recognizing = false;
					//this.stopRecognizer();
				}
			);
		}
	}

	stopRecognizer() {
		if (this.recognizer) {
			this.recognizing = false;
			this.recognizer.close();
			this.recognizer = null;
		}
	}

	recognizerCallback(s, e) {
		const reason = ResultReason[e.result.reason];

		if (reason === SpeechRecognizerResultReason.RecognizingSpeech) {
			//this.recognizing = false;
			//this.onInputPressEnter(e.result.text);
		}

		if (reason === SpeechRecognizerResultReason.RecognizedSpeech) {
			this.recognizing = false; // done as event not stopping on firefox
			this.onInputPressEnter(e.result.text);
		}

		if (reason === SpeechRecognizerResultReason.NoMatch) {
			this.recognizing = false; // done as event not stopping on firefox
			// this.addToConversation(
			// 	fmtMsg(this.locale.ConversationNoSpeechMessage),
			// 	ConversationAuthorType.Machine,
			// 	null
			// );

			const currentDialog = this.getActiveDialogById(this.currentDialogId);
			if (!isUndefined(currentDialog) && !isNull(currentDialog)) {
				this.moveToNext(
					this.getRetryOrSilentOrFallbackId(currentDialog),
					ConversationTemplateType.Silent,
					fmtMsg(this.locale.ConversationNoSpeechMessage)
				);
			} else {
				this.endConversation(fmtMsg(this.locale.ConversationNoSpeechMessage));
			}
		}
	}

	getRetryOrFallbackId(currentDialog) {
		if (this.canRetry(currentDialog)) {
			return this.retryDialodId;
		} else {
			return this.getFallbackPathId(currentDialog);
		}
	}

	getRetryOrSilentOrFallbackId(currentDialog) {
		if (this.canRetry(currentDialog)) {
			return this.retryDialodId;
		} else {
			return this.getSilentOrFallbackPathId(currentDialog);
		}
	}

	getSilentOrFallbackPathId(currentDialog: ConversationDialogChatModel) {
		const silentPath = currentDialog?.path.find(
			(i) => i.pathTypeId === PathTypes.Silent && i.statusId === ConversationStatus.Active
		);
		if (!isUndefined(silentPath) && silentPath.nextDialogId) {
			return silentPath.nextDialogId;
		} else {
			return this.getFallbackPathId(currentDialog);
		}
	}
}
